/**
 * CmCard: the care-manager hover/tap card on the capacity map.
 *
 * Positioning is the caller's job (CapacityMapPanel anchors this beside
 * the hovered/tapped pin's pixel point, 8px offset, no arrow); this
 * component is the card's content and look only, and says each fact
 * once: the name; the unit, on its own line underneath; the week's
 * new-client stance as the server sent it (new_client_stance: Open, Talk
 * to Me, Not Open or On leave) in words beside a dot in the stance's
 * fill, and no status line on a row sent without it; and a list of two
 * labelled facts, Last week (the hours worked) and Target ("Not set"
 * without one). A target is an
 * average over time, so nothing here subtracts one from the other. The
 * unit's own color already rides the pin's ring, so the card does not
 * repeat it as a dot, and it says nothing about home town or the unit's
 * counties; that is the map's business, not a hover card's.
 */

import { type ReactNode } from 'react';
import { STATED_AVAILABILITY_DOT, STATED_AVAILABILITY_LABEL, formatHours } from '@/lib/capacity';
import type { CmCapacityRowWire } from '@/lib/capacityApi';

/** The card palette's one accent not already a design-system token
 *  (matches api/_shared/email/brandShell.ts's CHAMPAGNE_GOLD, the same
 *  "Peace gold" used on the printed/emailed side of the brand). */
const GOLD_RULE_COLOR = '#B8893D';

export interface CmCardProps {
  row: CmCapacityRowWire;
}

export function CmCard({ row }: CmCardProps): ReactNode {
  const target = row.target_weekly_hours === null ? null : Number(row.target_weekly_hours);
  const stance = row.new_client_stance;
  const facts: [string, string][] = [
    ['Last week', `${formatHours(row.hours_worked_last_week) || '0'} h`],
    ['Target', target === null ? 'Not set' : `${formatHours(target)} h`],
  ];

  return (
    <div
      role="tooltip"
      data-testid="cm-card"
      data-roster-id={row.roster_id}
      className="pointer-events-none w-fit max-w-xs rounded-[10px] border border-sage-200 bg-cream px-3 py-2.5 shadow-sh2"
    >
      <p className="truncate text-sm font-semibold text-sage-800" data-testid="cm-card-name">
        {row.full_name}
      </p>
      {row.unit_name && (
        <p className="text-xs text-ink-soft" data-testid="cm-card-unit">
          {row.unit_name}
        </p>
      )}
      <div aria-hidden="true" className="my-1.5 h-[2px] w-8 rounded-pill" style={{ backgroundColor: GOLD_RULE_COLOR }} />
      {stance !== undefined && (
        <p className="flex items-center gap-1.5 text-xs font-semibold text-sage-800" data-testid="cm-card-status">
          <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: STATED_AVAILABILITY_DOT[stance] }} />
          {STATED_AVAILABILITY_LABEL[stance]}
        </p>
      )}
      <dl className="mt-0.5 grid gap-0.5 text-xs text-ink" data-testid="cm-card-facts">
        {facts.map(([term, value]) => (
          <div key={term} className="flex flex-wrap items-baseline gap-x-2">
            <dt className="text-label tracking-label font-semibold uppercase text-sage-400">{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
